import type { Permission } from '@tekansa/core/permissions';
import {
  Bot,
  Building2,
  ChartLine,
  Contact,
  Earth,
  Flame,
  FlaskConical,
  Gauge,
  House,
  KeyRound,
  LayoutDashboard,
  MailCheck,
  Mails,
  MessagesSquare,
  Rocket,
  ScrollText,
  Settings,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** One line shown on the "Coming soon" page until the feature is built. */
  comingSoon?: string;
  /** Sidebar section title (items without one sit at the top). */
  group?: 'Outreach' | 'Sending' | 'Insights' | 'Account' | 'Customers' | 'Platform';
  /** Only shown to roles with this permission. */
  permission?: Permission;
  /** Only shown to Super Admins (Tekansa staff). */
  platformAdmin?: boolean;
  /** Shown to Super Admins and Support (Step 15). */
  platformStaff?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/home', label: 'Home', icon: House },
  {
    to: '/campaigns',
    label: 'Campaigns',
    group: 'Outreach',
    icon: Rocket,
    permission: 'campaigns.view',
  },
  {
    to: '/leads',
    label: 'Leads',
    group: 'Outreach',
    icon: Contact,
    permission: 'leads.view',
  },
  {
    to: '/mailboxes',
    label: 'Mailboxes',
    icon: Mails,
    group: 'Sending',
    permission: 'mailboxes.view',
  },
  { to: '/domains', label: 'Domains', icon: Earth, group: 'Sending', permission: 'domains.view' },
  {
    to: '/sending',
    label: 'Sending',
    icon: Gauge,
    group: 'Sending',
    permission: 'deliverability.manage',
  },
  {
    to: '/deliverability',
    label: 'Deliverability',
    icon: MailCheck,
    group: 'Sending',
    permission: 'deliverability.view',
  },
  {
    to: '/inbox',
    label: 'Inbox',
    group: 'Outreach',
    icon: MessagesSquare,
    permission: 'inbox.view',
  },
  {
    to: '/reports',
    label: 'Reports',
    group: 'Insights',
    icon: ChartLine,
    permission: 'analytics.view',
  },
  { to: '/settings', label: 'Settings', icon: Settings, group: 'Account' },
];

/**
 * Step 17e: Super Admin's own menu (the Super Admin console, /admin). Never mixed with an
 * organisation's menu. Support sees the customer pages; the platform tools are Super Admin only.
 */
export const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, platformStaff: true },
  {
    to: '/admin/organisations',
    label: 'Organisations',
    icon: Building2,
    group: 'Customers',
    platformStaff: true,
  },
  { to: '/admin/users', label: 'Users', icon: Users, group: 'Customers', platformStaff: true },
  {
    to: '/admin/warmup',
    label: 'Warm-up pool',
    icon: Flame,
    group: 'Platform',
    platformAdmin: true,
  },
  {
    to: '/admin/seeds',
    label: 'Seed pool',
    icon: FlaskConical,
    group: 'Platform',
    platformAdmin: true,
  },
  { to: '/admin/ai', label: 'AI', icon: Bot, group: 'Platform', platformAdmin: true },
  {
    to: '/admin/audit-log',
    label: 'Audit log',
    icon: ScrollText,
    group: 'Platform',
    platformStaff: true,
  },
  {
    to: '/admin/account/profile',
    label: 'My profile',
    icon: UserRound,
    group: 'Account',
    platformStaff: true,
  },
  {
    to: '/admin/account/security',
    label: 'Password and security',
    icon: KeyRound,
    group: 'Account',
    platformStaff: true,
  },
];
